















































































import Vue from "vue";
import Page from "@/components/Page.vue";
import ConfirmationBox from "@/components/ConfirmationBox.vue";
import PartnerSearch from "@/modules/partner/views/PartnerSearch.vue";
import PartnerInfoCard from "@/modules/partner/components/PartnerInfoCard.vue";
import Partner from "@/modules/partner/models/Partner";
import PartnerApi from "@/modules/partner/services/PartnerApi";
import eventBus from "@/services/eventBus";

interface Data {
  transferPartner: Partner | null;
  keepPartner: Partner | null;
  // URL-ből (/partner-matching/:transferPartnerId?/:keepPartnerId?) előtöltött partnerek, a Mégse törli őket
  preloadedTransferPartner: Partner | null;
  preloadedKeepPartner: Partner | null;
  // előtöltésnél hány alapadat lista betöltésére várunk még, mielőtt a teljes oldalas töltés eltűnik
  pendingInitialCards: number;
  confirmationVisible: boolean;
  isBusy: boolean;
  resetCounter: number;
}

interface Methods {
  loadPreloadedPartners(): Promise<void>;
  onInfoLoaded(): void;
  loadPartnerById(id: number): Promise<Partner | null>;
  transferPartnerSelected(partner: Partner): void;
  keepPartnerSelected(partner: Partner): void;
  askConfirmation(): void;
  transfer(): void;
  cancel(): void;
}

interface Computed {
  canTransfer: boolean;
  validationErrors: string[];
}

/**
 * Partner összevezetés (TruMES PartnerManualMergeWindow portja).
 * A kivezetésre kerülő partner minden hivatkozása a megmaradó partnerre kerül,
 * a kivezetett partner "Téves rögzítés" státuszt kap.
 */
export default Vue.extend<Data, Methods, Computed, {}>({
  components: {
    Page,
    ConfirmationBox,
    PartnerSearch,
    PartnerInfoCard,
  },

  data() {
    return {
      transferPartner: null,
      keepPartner: null,
      preloadedTransferPartner: null,
      preloadedKeepPartner: null,
      pendingInitialCards: 0,
      confirmationVisible: false,
      isBusy: false,
      resetCounter: 0,
    };
  },

  computed: {
    // Minden ok, ami miatt az Átvezetés gomb tiltott: a piros felkiáltójel tooltipjében soronként jelenik meg
    validationErrors(): string[] {
      const errors: string[] = [];
      const transferId = this.transferPartner?.id ?? 0;
      const keepId = this.keepPartner?.id ?? 0;

      if (transferId <= 1) errors.push("Nincs kivezetésre kerülő partner kiválasztva!");
      if (keepId <= 1) errors.push("Nincs megmaradó partner kiválasztva!");
      if (transferId > 1 && keepId > 1 && transferId === keepId) {
        errors.push("A kivezetésre kerülő és a megmaradó partner nem lehet azonos!");
      }
      return errors;
    },

    canTransfer(): boolean {
      return this.validationErrors.length === 0 && !this.isBusy;
    },
  },

  mounted() {
    this.loadPreloadedPartners();
  },

  methods: {
    // TruMES-ben a partner karbantartóból nyílik az ablak, a kivezetendő előtöltve; itt az URL paraméterek töltik elő
    // mindkét partnert (pl. Kanri beemelési hibából érkező linknél a megmaradó is adott)
    async loadPreloadedPartners() {
      const transferId = Number(this.$route.params.transferPartnerId ?? 0);
      const keepId = Number(this.$route.params.keepPartnerId ?? 0);
      const hasPreload = transferId > 1 || keepId > 1;
      if (!hasPreload) return;

      // teljes oldalas töltés, amíg a partnerek és az alapadat listáik is be nem töltöttek
      eventBus.$emit("busy-message", true);
      try {
        const [transfer, keep] = await Promise.all([this.loadPartnerById(transferId), this.loadPartnerById(keepId)]);
        this.preloadedTransferPartner = transfer;
        this.preloadedKeepPartner = keep;
        this.transferPartner = transfer;
        this.keepPartner = keep;
        this.pendingInitialCards = [transfer, keep].filter((p) => !!p).length;
      } catch {
        this.pendingInitialCards = 0;
      } finally {
        if (this.pendingInitialCards === 0) eventBus.$emit("busy-message", false);
      }
    },

    // egy alapadat lista betöltött; előtöltésnél az utolsó után tűnik el a teljes oldalas töltés
    onInfoLoaded() {
      if (this.pendingInitialCards <= 0) return;
      this.pendingInitialCards--;
      if (this.pendingInitialCards === 0) eventBus.$emit("busy-message", false);
    },

    async loadPartnerById(id: number): Promise<Partner | null> {
      if (!id || id <= 1) return null;
      try {
        return await PartnerApi.get(id);
      } catch {
        return null;
      }
    },

    transferPartnerSelected(partner: Partner) {
      this.transferPartner = partner && partner.id > 1 ? partner : null;
    },

    keepPartnerSelected(partner: Partner) {
      this.keepPartner = partner && partner.id > 1 ? partner : null;
    },

    askConfirmation() {
      if (!this.canTransfer) return;
      this.confirmationVisible = true;
    },

    async transfer() {
      this.confirmationVisible = false;
      if (!this.canTransfer || !this.transferPartner || !this.keepPartner) return;

      this.isBusy = true;
      // teljes oldalas overlay (App szintű BusyIndicator), amíg az átvezetés fut
      eventBus.$emit("busy-message", true);
      try {
        const ok = await PartnerApi.mergePartners(this.transferPartner.id, this.keepPartner.id);
        if (ok) {
          eventBus.$emit("success", "Sikeres összevezetés");
          this.cancel();
        }
      } catch {
        // a hibaüzenetet a checkResponse már megjelenítette
      } finally {
        eventBus.$emit("busy-message", false);
        this.isBusy = false;
      }
    },

    // Mégse: mindkét mező kiürül, az URL-ből jött előtöltés is törlődik; az oldal nem záródik be
    cancel() {
      this.confirmationVisible = false;
      this.preloadedTransferPartner = null;
      this.preloadedKeepPartner = null;
      this.transferPartner = null;
      this.keepPartner = null;
      this.resetCounter++;
    },
  },
});
